import React, {FC, useState, useEffect} from "react";
import {useParams, useHistory} from "react-router-dom";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {
  FormControl,
  FormLabel,
  Button,
  Grid,
  TextField,
  Switch,
  Box,
  MenuItem,
} from "@material-ui/core";

import {
  Highlight,
  getHighlight,
  updateHighlight,
  deleteHighlight,
  addHighlight,
} from "../../app/models/highlight";

import {
  FormIndicator
} from "./components/indicator";

import {toast} from "react-toastify";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& > *": {
        margin: theme.spacing(1),
      },
    },
    spacing: {
      margin: "0.25rem",
    }
  })
);

const HighlightsDetailsPage = () => {
  const history = useHistory();
  const classes = useStyles();
  const {id} = useParams<Highlight>();

  const emptyHighlight = {
    id: null,
    text: "",
    category: "defi",
    sql_query: "",
    active: false,
  }
  const [highlight, setHighlight] = useState<Highlight>(emptyHighlight);

  const [formIndicator, setFormIndicator] = useState<FormIndicator>({})
  const [phase, setPhase] = useState<"ready" | "loading">("ready");

  const asyncLoadHighlight = async (highlightId: number) => {
    setPhase("loading");
    try {
      const highlight = await getHighlight(highlightId);
      setHighlight(highlight);
    } catch (e) {
      console.log("Error loading highlight", e);
    } finally {
      setPhase("ready");
    }
  }

  useEffect(() => {
    if (id === "create") {
      setHighlight(emptyHighlight);
    } else {
      asyncLoadHighlight(parseInt(id));
    }
  }, [id]);

  const handleInputValue = (e: React.ChangeEvent) => {
    const {name, value} = e.target;
    if (name) {
      setHighlight({...highlight, [name]: (value as string) || ""});
    }
  };
  const handleSwitchValue = (e: React.ChangeEvent) => {
    const name = e.target.name;
    const value = e.target.checked;
    if (name) {
      setHighlight({...highlight, [name]: (value as boolean) || false});
    }
  };
  const validate = (editedModel: Highlight) => {
    const errors: FormIndicator = {};
    if (editedModel.text.trim() == "") {
      errors["text"] = {
        error: true,
        helperText: "Text cannot be empty",
      }
    }

    if (editedModel.sql_query.trim() == "") {
      errors["sql_query"] = {
        error: true,
        helperText: "sql_query cannot be empty",
      }
    }

    setFormIndicator(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSave = React.useCallback(async (next: "edit" | "new" | "list") => {
    if (validate(highlight)) {
      setPhase("loading");
      try {
        let result = {}
        if (highlight.id) {
          result = await updateHighlight("/highlights/" + highlight.id, highlight)
        } else {
          result = await addHighlight(highlight)
        }
        switch (next) {
          case "new":
            setHighlight(emptyHighlight);
            history.push("/admin/highlights/create");
            break;
          case "edit":
            history.push("/admin/highlights/" + result.id);
            break;
          case "list":
            history.push("/admin/highlights/");
            break;
        }

        toast.success(`saving ${highlight?.text} succeeded`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
        });


      } catch (e) {
        toast.error("error saving: " + e.error_msg, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
        })
      } finally {
        setPhase("ready");
      }
    }
  }, [highlight])

  const handleDelete = async (evt: React.MouseEventHandler<HTMLButtonElement>) => {
    setPhase("loading");
    try {
      await deleteHighlight("/highlights/" + highlight.id)
      history.push("/admin/highlights");
      toast.success("Delete succeeded", {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      });
    } catch (e) {
      toast.error("error delete: " + e.error_msg, {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
      })
    } finally {
      setPhase("ready");
    }
  };

  return (
    <form className={classes.root} noValidate autoComplete="off">
      <FormControl className="u-margin-right-ui-1 u-width-fc-500">
        <TextField
          required
          label="Text"
          variant="outlined"
          value={highlight?.text}
          onChange={handleInputValue}
          name="text"
          {...formIndicator['text']}
        />
      </FormControl>
      <br/>

      <FormControl className="u-margin-right-ui-1 u-width-fc-500">
        <FormLabel id="select-category-label">Category
          <Box sx={{marginLeft: '20px', display: "inline"}}>
            <Select
              labelId="select-category-label"
              id="select-category"
              value={highlight?.category || "defi"}
              label="Category"
              name="category"
              onChange={handleInputValue}
            >
              <MenuItem value={"defi"}>DeFi</MenuItem>
              <MenuItem value={"nft"}>NFT</MenuItem>
            </Select>
          </Box>
        </FormLabel>
      </FormControl>
      <br/>

      <FormControl className="u-margin-right-ui-1 u-width-fc-500">
        <TextField
          required
          label="Query (sql)"
          variant="outlined"
          value={highlight?.sql_query}
          onChange={handleInputValue}
          name="sql_query"
          {...formIndicator['sql_query']}
        />
      </FormControl>
      <br/>
      <FormControl className="u-margin-right-ui-1 u-width-fc-500">
        <FormLabel>Active
          <Switch
            checked={highlight?.active}
            onChange={handleSwitchValue}
            name="active"/>
        </FormLabel>
      </FormControl>

      {phase === "ready" ? (
        <Grid
          container
          direction="row"
          alignItems="stretch"
          justifyContent="space-between"
        >
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSave("edit")}
            >
              Save
            </Button>
            &nbsp;
            <Button
              variant="contained"
              color="default"
              onClick={() => handleSave("new")}
            >
              Save and Add another
            </Button>

            &nbsp;
            <Button
              variant="contained"
              color="default"
              onClick={() => handleSave("list")}
            >
              Save and go back to the list
            </Button>
          </Grid>

          {highlight.id !== null && (
            <Grid item>
              <Button variant="contained" color="secondary" onClick={handleDelete}>
                Delete
              </Button>
            </Grid>)}
        </Grid>
      ) : (
        <CircularProgress/>
      )}
    </form>
  );
};

export default HighlightsDetailsPage;
